// PlatformSmtpTab — fourth tab on SuperSystemPage.
//
// Reads + writes platform_smtp_settings (used exclusively for cron failure
// alerts to super admins). The password column is masked on read; on write
// the field is optional — omitting it keeps the existing ciphertext.

import { useEffect, useState } from 'react';
import { Card } from '../../../../components/ui/Card/Card';
import { Spinner } from '../../../../components/ui/Spinner/Spinner';
import { Button } from '../../../../components/ui/Button/Button';
import {
  usePlatformSmtp,
  useUpsertPlatformSmtp,
  type UpsertPlatformSmtpInput,
} from '../../../../hooks/useSystemLogs';
import styles from './PlatformSmtpTab.module.css';

const DEFAULT_FROM_NAME = 'Tricivo Platform';
const DEFAULT_PORT = 587;
// POST /api/v1/super/platform-smtp/test is not implemented server-side
// (see Prompt A scope). Keep the button visible but disabled with a
// tooltip so the affordance survives if/when the endpoint lands.
const TEST_EMAIL_ENDPOINT_AVAILABLE = false;

interface FormState {
  host: string;
  port: number;
  secure: boolean;
  username: string;
  password: string;
  from_address: string;
  from_name: string;
}

function emptyForm(): FormState {
  return {
    host: '',
    port: DEFAULT_PORT,
    secure: false,
    username: '',
    password: '',
    from_address: '',
    from_name: DEFAULT_FROM_NAME,
  };
}

export function PlatformSmtpTab(): JSX.Element {
  const query = usePlatformSmtp();
  const upsert = useUpsertPlatformSmtp();
  const [form, setForm] = useState<FormState>(emptyForm);
  const [hydrated, setHydrated] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!query.data || hydrated) return;
    if (query.data.configured) {
      setForm({
        host: query.data.host ?? '',
        port: query.data.port ?? DEFAULT_PORT,
        secure: query.data.secure,
        username: query.data.username ?? '',
        password: '',
        from_address: query.data.from_address ?? '',
        from_name: query.data.from_name ?? DEFAULT_FROM_NAME,
      });
    } else {
      setForm(emptyForm());
    }
    setHydrated(true);
  }, [query.data, hydrated]);

  const passwordPlaceholder = query.data?.configured
    ? 'Leave blank to keep current'
    : '';

  function update<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((prev) => ({ ...prev, [key]: value }));
    setSuccess(null);
    setError(null);
  }

  async function onSave() {
    setSuccess(null);
    setError(null);
    const body: UpsertPlatformSmtpInput = {
      host: form.host,
      port: form.port,
      secure: form.secure,
      username: form.username,
      from_address: form.from_address,
      from_name: form.from_name,
    };
    if (form.password.length > 0) {
      body.password = form.password;
    }
    try {
      await upsert.mutateAsync(body);
      setSuccess('SMTP settings saved.');
      setForm((prev) => ({ ...prev, password: '' }));
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Failed to save SMTP settings.';
      setError(message);
    }
  }

  if (query.isLoading) {
    return (
      <Card>
        <div className={styles.empty}>
          <Spinner />
        </div>
      </Card>
    );
  }

  const isConfigured = query.data?.configured === true;

  return (
    <Card flush>
      <div className={styles.tab}>
        <div className={styles.statusRow}>
          <span
            className={styles.statusBadge}
            data-configured={isConfigured ? 'true' : 'false'}
            role="status"
          >
            {isConfigured ? 'Configured' : 'Not configured'}
          </span>
        </div>
        {success !== null ? (
          <div className={styles.success} role="status">{success}</div>
        ) : null}
        {error !== null ? (
          <div className={styles.error} role="alert">{error}</div>
        ) : null}

        <div className={styles.form}>
          <div className={styles.field}>
            <label htmlFor="smtp-host">SMTP Host</label>
            <input
              id="smtp-host"
              type="text"
              className={styles.input}
              value={form.host}
              onChange={(e) => update('host', e.target.value)}
              required
            />
          </div>
          <div className={styles.field}>
            <label htmlFor="smtp-port">Port</label>
            <input
              id="smtp-port"
              type="number"
              className={styles.input}
              value={form.port}
              onChange={(e) => update('port', Number(e.target.value))}
              min={1}
              max={65535}
            />
          </div>
          <div className={styles.checkboxRow}>
            <input
              id="smtp-secure"
              type="checkbox"
              checked={form.secure}
              onChange={(e) => update('secure', e.target.checked)}
            />
            <label htmlFor="smtp-secure">Secure (TLS)</label>
          </div>
          <div className={styles.field}>
            <label htmlFor="smtp-username">Username</label>
            <input
              id="smtp-username"
              type="text"
              className={styles.input}
              value={form.username}
              onChange={(e) => update('username', e.target.value)}
              required
              autoComplete="off"
            />
          </div>
          <div className={styles.field}>
            <label htmlFor="smtp-password">Password</label>
            <input
              id="smtp-password"
              type="password"
              className={styles.input}
              value={form.password}
              onChange={(e) => update('password', e.target.value)}
              placeholder={passwordPlaceholder}
              autoComplete="new-password"
            />
          </div>
          <div className={styles.field}>
            <label htmlFor="smtp-from-address">From Address</label>
            <input
              id="smtp-from-address"
              type="email"
              className={styles.input}
              value={form.from_address}
              onChange={(e) => update('from_address', e.target.value)}
              required
            />
          </div>
          <div className={styles.field}>
            <label htmlFor="smtp-from-name">From Name</label>
            <input
              id="smtp-from-name"
              type="text"
              className={styles.input}
              value={form.from_name}
              onChange={(e) => update('from_name', e.target.value)}
            />
          </div>
        </div>

        <div className={styles.actions}>
          <Button variant="primary" onClick={onSave} loading={upsert.isPending}>
            Save SMTP settings
          </Button>
          <Button
            variant="secondary"
            disabled={!TEST_EMAIL_ENDPOINT_AVAILABLE}
            title={
              TEST_EMAIL_ENDPOINT_AVAILABLE
                ? undefined
                : 'Test endpoint not yet implemented (deferred)'
            }
          >
            Send Test Email
          </Button>
        </div>

        <p className={styles.footnote}>
          Used by the platform for super-admin cron failure alerts. Separate
          from per-tenant communication settings.
        </p>
      </div>
    </Card>
  );
}
